import { isBefore, startOfDay } from 'date-fns'
import { ConfigurationVersions } from 'engageTools/studio/schema'
import { t } from 'translations'

interface StatusMessage {
    status: 'disabled' | 'success' | 'error' | 'blue' | 'warn'
    helperStatus: Array<'upcoming' | 'expired' | 'duplicate'>
    publishState: boolean
}

export const getStatus = (tool: Partial<ConfigurationVersions>): StatusMessage => {
    const helperStatus: StatusMessage['helperStatus'] = []
    let status: StatusMessage['status'] = 'disabled'
    let publishState = false
    const isToolExpired = tool.validTo && isBefore(startOfDay(tool.validTo), startOfDay(new Date()))

    if (tool.current && !!tool.current.publishedAt) {
        status = 'success'
        publishState = true
    } else if (!tool.current && !!tool.next && !!tool.next.unpublishedAt) {
        status = 'error'
        publishState = false
    } else if (!!tool.next && !!tool.next.createdAt) {
        status = 'disabled'
        publishState = false
    }

    if (tool.validFrom && new Date(tool.validFrom) > new Date()) {
        status = 'blue'
        helperStatus.push('upcoming')
    } else if (isToolExpired) {
        status = 'warn'
        helperStatus.push('expired')
    }

    if (!!tool?.duplicates?.length) {
        status = 'warn'
        helperStatus.push('duplicate')
    }

    return {
        status,
        publishState,
        helperStatus,
    }
}

// Status text without the ", duplicate" suffix; the status cell renders that suffix as a link.
export const getStatusLabel = (status: StatusMessage): string => {
    let base: 'inactive' | 'published' | 'unpublished' = 'inactive'

    if (status.status === 'success' || status.publishState) {
        base = 'published'
    } else if (status.status === 'error') {
        base = 'unpublished'
    }

    if (status.helperStatus?.includes('upcoming')) {
        switch (base) {
            case 'published':
                return t('engageTools.overview.status.upcomingPublished')
            case 'unpublished':
                return t('engageTools.overview.status.upcomingUnpublished')
            default:
                return t('engageTools.overview.status.upcomingInactive')
        }
    } else if (status.helperStatus?.includes('expired')) {
        switch (base) {
            case 'published':
                return t('engageTools.overview.status.expiredPublished')
            case 'unpublished':
                return t('engageTools.overview.status.expiredUnpublished')
            default:
                return t('engageTools.overview.status.expiredInactive')
        }
    }

    switch (base) {
        case 'published':
            return t('engageTools.overview.status.published')
        case 'unpublished':
            return t('engageTools.overview.status.unpublished')
        default:
            return t('engageTools.overview.status.inactive')
    }
}

export const formatStatusMessage = (status: StatusMessage): string => {
    const message = getStatusLabel(status)

    if (status.helperStatus?.includes('duplicate')) {
        return t('engageTools.overview.status.withDuplicate', { status: message })
    }

    return message
}
